import { z } from 'zod'
import {
  EMAIL_AUDIT_COPY_FIELD_MAX_LENGTH,
  EMAIL_AUDIT_RESPONSE_SCHEMA,
} from '@/lib/email/audit/contracts'

/**
 * The free email audit's wire contract, shared by the page's client island,
 * the anonymous route, the inbound-email handler and the report page.
 * Client-safe: plain zod and constants.
 */

export const FREE_EMAIL_AUDIT_API_PATH = '/api/free-tools/email-audit'
export const FREE_EMAIL_AUDIT_ADDRESS = 'audit@email.brew.new'

/**
 * Largest email HTML the free audit accepts. Gmail clips a message past
 * 102 KB, so a real email that needs more than this is already broken; the
 * cap keeps an anonymous request well inside a function's request limit.
 */
export const FREE_EMAIL_AUDIT_MAX_HTML_BYTES = 1024 * 1024
/** JSON escaping can double quote-heavy HTML; the body cap allows for it. */
export const FREE_EMAIL_AUDIT_MAX_BODY_BYTES =
  FREE_EMAIL_AUDIT_MAX_HTML_BYTES * 2 + 16 * 1024

export function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength
}

export function isFreeEmailAuditHtmlTooLarge(html: string): boolean {
  return utf8ByteLength(html) > FREE_EMAIL_AUDIT_MAX_HTML_BYTES
}

export const FREE_EMAIL_AUDIT_REQUEST_SCHEMA = z
  .object({
    /** Size is checked apart (`isFreeEmailAuditHtmlTooLarge`): its own 413. */
    html: z.string().refine((value) => value.trim().length > 0, {
      message: 'Paste or upload the email HTML.',
    }),
    subject: z.string().max(EMAIL_AUDIT_COPY_FIELD_MAX_LENGTH).optional(),
    previewText: z.string().max(EMAIL_AUDIT_COPY_FIELD_MAX_LENGTH).optional(),
  })
  .strict()

export type FreeEmailAuditRequest = z.infer<
  typeof FREE_EMAIL_AUDIT_REQUEST_SCHEMA
>

/**
 * `full` ran every check; `quick` ran only the instant checks (the rules and
 * client support, with no paid call) because the full-check pool was busy.
 */
export const FREE_EMAIL_AUDIT_MODE_SCHEMA = z.enum(['full', 'quick'])
export type FreeEmailAuditMode = z.infer<typeof FREE_EMAIL_AUDIT_MODE_SCHEMA>

export const FREE_EMAIL_AUDIT_CHANNEL_SCHEMA = z.enum(['web', 'email'])
export type FreeEmailAuditChannel = z.infer<
  typeof FREE_EMAIL_AUDIT_CHANNEL_SCHEMA
>

export const FREE_EMAIL_AUDIT_REPORT_SCHEMA = z
  .object({
    reportId: z.string().min(16).max(64),
    channel: FREE_EMAIL_AUDIT_CHANNEL_SCHEMA,
    mode: FREE_EMAIL_AUDIT_MODE_SCHEMA,
    createdAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    /** The email arrived forwarded, so a mail client rewrote its HTML. */
    wasForwarded: z.boolean().optional(),
    /**
     * A partial audit's score over everything its checks found (every
     * occurrence, capped per rule), which the engine's response withholds.
     */
    inventoryScore: z.number().int().min(0).max(100).optional(),
    audit: EMAIL_AUDIT_RESPONSE_SCHEMA,
  })
  .strict()

export type FreeEmailAuditReport = z.infer<
  typeof FREE_EMAIL_AUDIT_REPORT_SCHEMA
>

export const FREE_EMAIL_AUDIT_ERROR_CODES = [
  'invalid',
  'too_large',
  'forbidden',
  'rate_limited',
  'unavailable',
  'failed',
] as const

export type FreeEmailAuditErrorCode =
  (typeof FREE_EMAIL_AUDIT_ERROR_CODES)[number]

export type FreeEmailAuditApiResponse =
  | { report: FreeEmailAuditReport }
  | {
      error: {
        code: FreeEmailAuditErrorCode
        message: string
        retryAfter?: number
      }
    }

export function freeEmailAuditReportPath(reportId: string): string {
  return `/email-audit/r/${reportId}`
}
